import { useEffect, useRef, useState } from "react";
import { Mic, Square, Keyboard, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

function pickMime() {
  const MR = window.MediaRecorder;
  if (!MR) return null;
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    if (MR.isTypeSupported && MR.isTypeSupported(t)) return t;
  }
  return null;
}

// Speak and Type are EQUAL choices (segmented toggle); the last choice is
// remembered per device. Mic-denied/unsupported falls back to Type.
export default function VoiceRecorder({ value, onChange, lang = "en", placeholder = "", typePlaceholder = "" }) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [supported, setSupported] = useState(true);
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("bf.log.mode") || "speak"; } catch { return "speak"; }
  });
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) setSupported(false);
    return () => stopStream();
  }, []);

  const choose = (m) => {
    setMode(m);
    try { localStorage.setItem("bf.log.mode", m); } catch { /* ignore */ }
  };

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((tr) => tr.stop());
    streamRef.current = null;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mimeRef.current = pickMime();
      const rec = mimeRef.current ? new MediaRecorder(stream, { mimeType: mimeRef.current }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data?.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: mimeRef.current || "audio/webm" });
        if (blob.size < 500) return;
        const ext = (mimeRef.current || "").includes("mp4") ? "m4a" : "webm";
        const fd = new FormData();
        fd.append("lang", lang.startsWith("es") ? "es" : "en");
        fd.append("file", new File([blob], `log.${ext}`, { type: blob.type }));
        setTranscribing(true);
        try {
          const { data } = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
          const next = [value, data.text].filter(Boolean).join(" ").trim();
          onChange(next);
        } catch (e) {
          toast.error(t("toast_transcribe_failed"));
        }
        setTranscribing(false);
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error(t("toast_mic_unavailable"));
      setSupported(false);
      choose("type");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (e) { /* ignore */ }
    setRecording(false);
  };

  const showType = mode === "type" || !supported;

  return (
    <div className="space-y-3">
      {/* Equal-choice mode toggle (selection persists per device) */}
      <div className="grid grid-cols-2 gap-1 rounded-full border border-slate-600 bg-slate-800 p-1" data-testid="log-mode-toggle">
        <button type="button" onClick={() => choose("speak")} disabled={!supported}
          data-testid="log-mode-speak" aria-pressed={!showType}
          title={!supported ? t("toast_mic_unavailable") : undefined}
          className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full text-sm font-bold transition-colors ${!showType ? "bg-brand text-white" : "text-slate-300 hover:text-white"} disabled:opacity-40`}>
          <Mic className="h-4 w-4" /> {t("log_speak")}
        </button>
          <button type="button" onClick={() => choose("type")}
          data-testid="log-mode-type" aria-pressed={showType}
          className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full text-sm font-bold transition-colors ${showType ? "bg-brand text-white" : "text-slate-300 hover:text-white"}`}>
          <Keyboard className="h-4 w-4" /> {t("log_type")}
        </button>
      </div>

      {showType ? (
        <Textarea
          data-testid="log-textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={showType ? (typePlaceholder || t("vr_type_ph")) : (placeholder || t("log_empty_hint"))}
          rows={5}
          className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-brand"
        />
      ) : (
        <div className="flex flex-col items-center gap-4 py-6">
          <button
            type="button"
            data-testid="log-mic"
            aria-label={recording ? t("log_stop") : t("log_speak")}
            onClick={recording ? stop : start}
            disabled={transcribing}
            className={`relative h-24 w-24 rounded-full flex items-center justify-center transition-transform active:scale-95 bf-brand-shadow disabled:opacity-70 ${
              recording ? "bg-red-500 animate-pulse" : "bg-brand"
            }`}
          >
            {transcribing ? (
              <Loader2 className="h-9 w-9 text-white animate-spin" />
            ) : recording ? (
              <Square className="h-9 w-9 text-white" fill="white" />
            ) : (
              <Mic className="h-10 w-10 text-white" />
            )}
          </button>
          <div className="text-sm font-semibold text-slate-200" data-testid="log-mic-status">
            {transcribing ? t("log_transcribing") : recording ? t("log_stop") : t("log_hold_to_talk")}
          </div>
          {value && (
            <div className="w-full rounded-xl border border-slate-700 bg-slate-900 p-4 text-sm text-slate-100 whitespace-pre-wrap" data-testid="log-transcript">
              {value}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
